/* eslint-disable no-continue */
/* eslint-disable no-useless-return */
/* eslint-disable default-case */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { AgGridReact } from "ag-grid-react";
import { ColDef, GridApi, SideBarDef } from "ag-grid-community";
import { useFormikContext } from "formik";
import { Dialog } from "@mui/material";
import { Icon } from "@iconify/react";
import _ from "lodash";
import { toast } from "react-toastify";
import { Button, ChevronDown, Filter, Search, X } from "./utils/ComponentUtils";
import {
  Box,
  ButtonCancelSecondary,
  ButtonPrimary,
  ButtonPrimary1,
  Center,
  getGrantStrikePrice,
  HStack,
  VStack,
} from "../../../components/utils";
import {
  BlackOutDay,
  BlackOutDayFilter,
  BlackOutDayResponse,
  EmployeeOfferDetails,
  NameAndId,
  TransactionDetailsData,
} from "../../../types/Transaction";
import { ChvronDown } from "../../../utils/assets/chevronDown";
import { formatDisplayDate } from "../../../utils/date";
import SearchInput from "../../../components/shared/SearchInput";
import FilterOptions from "./utils/FilterOptions";
import Avatar from "../../esopOverview/Avatar";
import AddParticipantPopUp from "./utils/AddParticipantPopUp";
import {
  formatCurrency,
  getCurrencySymbol,
  getCurrencyType,
} from "../../../utils/currencyFormatter";
import { MonetaryEventState, OfferState } from "../../../types/OptionBuyback";
import {
  useCloseExerciseTransaction,
  useGetBlackOutDays,
} from "../../../queries/transactionV2";
import { useGetEmailSettings } from "../../../queries/siteSettings";
import TransactionStatusLabel from "./utils/TransactionStatusLabel";
import {
  changeOfferStatus,
  deleteEmployeeFromTransaction,
  sendReminderEmails,
  resetEmployeeWillingness,
} from "../../../api/TransactionApisV2";
import AlertDialog from "../../../components/shared/AlertDialog";
import { Action, CTADropdown } from "../../../components/shared/Dropdown";
import DocumentsUploaded from "./utils/DocumentsUploaded";
import StatusChangeDialog from "./utils/StatusChangeDialog";
import ResetWillingnessDialog from "./utils/ResetWillingnessDialog";
import { BulkActions } from "../../esopOverview/GrantsTable";
import ReminderEmailDialog from "./utils/ReminderEmailDialog";
import AddExternalPaymentDetails from "./utils/AddExternalPaymentDetails";
import {
  SummaryCardType,
  SummaryTransactionCards,
} from "./utils/SummaryTransactionCards";

interface TransactionStartedDetailsProps {
  onStepChange: (e?: number) => void;
  onBackClick?: () => void;
}

interface FilterType {
  id: string;
  label: string;
  options?: string[];
  type: string;
}

interface Employee {
  id: number;
  name: string;
  position: string;
  hrId: string;
  vestedOptions: number;
  eligibility: number;
  willingness: string;
  status: string;
  avatar: string;
}

interface FilterState {
  department: string[];
  vestedOptions: {
    min: number;
    max?: number;
  };
  eligibleOptions: {
    min: number;
    max?: number;
  };
  employmentType: string[];
  tenure: {
    min: number;
    max?: number;
  };
  participation: string;
}

export const SummaryTransactionCard = ({
  mainHeader,
  header,
  value,
  subHeader,
}: {
  mainHeader: string;
  header: string;
  value: string;
  subHeader?: string;
}) => (
  <VStack className="items-center justify-center p-4 text-center border border-gray-200 rounded-lg shadow-box">
    <p className="p-1 text-base font-medium form-label">{header}</p>
    <p className="px-4 text-lg font-semibold">{value}</p>
    <p className="p-1 text-sm font-normal form-label">{mainHeader}</p>
    {subHeader && (
      <p className="p-1 px-4 text-xs font-normal text-gray-400">{subHeader}</p>
    )}
  </VStack>
);

export function getEmployeeBlackout(
  employee: EmployeeOfferDetails,
  blackoutDayIds: string[],
  allBlackoutDays: BlackOutDay[],
  date: Date
): BlackOutDayResponse | null {
  for (const blackoutId of blackoutDayIds) {
    const blackoutDay = allBlackoutDays.find((b) => b.id === blackoutId);
    if (!blackoutDay) continue;
    const inBetweenDates =
      new Date(blackoutDay.startDate) <= date &&
      new Date(blackoutDay.endDate) >= date;
    if (!inBetweenDates) continue;
    const employeesList = blackoutDay.designatedPerson?.employeesList || [];
    const isInList = employeesList.some(
      (emp: any) =>
        emp.employeeId === employee.employeeId || emp.id === employee.employeeId
    );
    if (isInList) {
      return {
        id: blackoutDay.id || "",
        startDate: new Date(blackoutDay.startDate),
        endDate: new Date(blackoutDay.endDate),
        reason: blackoutDay.reason,
        employeeId: employee.employeeId,
        exists: true,
      };
    }
  }
  return null;
}

export function getReminderEmailNotes(emailSettings?: {
  emailsToBeSent?: boolean;
  sendEmailsToEmployees?: boolean;
}): React.ReactElement | null {
  const bothOff =
    !emailSettings?.emailsToBeSent && !emailSettings?.sendEmailsToEmployees;
  const msg = bothOff
    ? 'Reminder emails cannot be sent — enable "Allow Notification Emails" and "Allow Hissa to send notification Emails to Employees" in Email Settings.'
    : !emailSettings?.emailsToBeSent
    ? 'Reminder emails cannot be sent — enable "Allow Notification Emails" in Email Settings.'
    : !emailSettings?.sendEmailsToEmployees
    ? 'Reminder emails won\'t reach employees — enable "Allow Hissa to send notification Emails to Employees" in Email Settings.'
    : null;
  return msg ? (
    <p className="italic text-gray-500 mt-2 mb-1">Note: {msg}</p>
  ) : null;
}

const TransactionStartedDetails = (props: TransactionStartedDetailsProps) => {
  // Filter states

  const currencySymbol = getCurrencySymbol();
  const currencyType = getCurrencyType();
  const { data: allBlackoutDays } = useGetBlackOutDays();
  const { data: emailSettings } = useGetEmailSettings();
  const formik = useFormikContext<TransactionDetailsData>();
  const { mutate: closeTransaction, isLoading: isClosing } =
    useCloseExerciseTransaction();

  const [deleting, setDeleting] = useState(false);
  const [closing, setClosing] = useState(false);
  const [adding, setAdding] = useState(false);

  const [showParticipantList, setShowParticipantList] = useState(true);

  const gridApi = useRef<GridApi | any>(null);
  const onGridReady = (params: any) => {
    gridApi.current = params;
  };

  const [attachDocumentsDialog, setAttachDocumentsDialog] = useState<{
    open: boolean;
    employeeId?: string;
  }>({ open: false, employeeId: undefined });

  const [dialog, setDialog] = useState({
    open: false,
    data: undefined,
  });

  function EmployeeInfoRender(emp: any) {
    return (
      <HStack>
        <Avatar
          name={emp.data.employeeName}
          status={emp.data.employmentStatus}
        />
        <Box className="ml-2">
          <p className="text-xs font-medium text-gray-dark">
            {emp.data.employeeName}
          </p>
          <p className="capitalize text-xxs text-gray-light">
            {emp.data.hrId || ""}
          </p>
        </Box>
      </HStack>
    );
  }

  function PreviewListStatus(status: any) {
    if (
      status === OfferState.DRAFT ||
      status === OfferState.OFFERED ||
      !status
    ) {
      return (
        <>
          <TransactionStatusLabel state={status || "DRAFT"} />
        </>
      );
    }
    return (
      <HStack>
        <TransactionStatusLabel
          state={showParticipantList ? "REQUESTED" : status}
        />
      </HStack>
    );
  }

  function RequestedCellRender(params: any) {
    return (
      <VStack className="items-center justify-center gap-1">
        <p
          className={`text-xs  text-gray-dark ${
            params.data?.isTotalRow ? "font-semibold" : "font-medium"
          }`}
        >
          {(params.totalWillingness || 0).toLocaleString(currencyType)}
        </p>
        {!params.data?.isTotalRow && (
          <p className="text-xs font-medium text-gray-400">
            {parseFloat(
              (
                (params.totalWillingness / params.totalEligibleOptions) *
                100
              ).toFixed(4)
            )}
            %
          </p>
        )}
      </VStack>
    );
  }

  const [statusDialog, setStatusDialog] = useState<{
    open: boolean;
    employees?: EmployeeOfferDetails[];
  }>({ open: false, employees: undefined });

  const [statusLoading, setStatusLoading] = useState(false);
  const [metaData, setMetaData] = useState<any>();

  const handleStatusChange = (status: OfferState) => {
    if (!statusDialog.employees) return;
    setStatusLoading(true);
    changeOfferStatus({
      employees: statusDialog.employees.map((emp) => emp.employeeId),
      transactionId: formik.values.id || "",
      status,
      date: undefined,
      metaData,
    })
      .then((response: any) => {
        setStatusDialog({ open: false, employees: undefined });
        formik.setFieldValue("previewList", response.data.previewList);
        formik.setFieldValue(
          "selectedEmployeesList",
          response.data.selectedEmployeesList
        );
        toast.success("Status updated!");
      })
      .catch((err) => {
        toast.error(err?.response?.data?.errorRaw || "Failed to update status");
      })
      .finally(() => setStatusLoading(false));
  };

  const [paymentDialog, setPaymentDialog] = useState<{
    open: boolean;
    approval: boolean;
    employee?: EmployeeOfferDetails;
  }>({ open: false, approval: false, employee: undefined });

  const [resetDialog, setResetDialog] = useState({
    open: false,
    employee: undefined as EmployeeOfferDetails | undefined,
  });

  const [resetLoading, setResetLoading] = useState(false);

  const handleAction = (employee: EmployeeOfferDetails, action: Action) => {
    if (action.disabled) {
      return;
    }
    if (action.name === "Delete") {
      setOpen({
        open: true,
        data: {
          id: employee.employeeId,
          name: employee.employeeName,
          hrId: employee.hrId,
        },
      });
    }
    if (action.name === "Change Status") {
      const blackoutDay = getEmployeeBlackout(
        employee,
        formik.values.monetaryDetails.blackOutDays || [],
        allBlackoutDays || [],
        new Date()
      );
      if (blackoutDay && blackoutDay.exists) {
        toast.error(
          `Employee is in blackout period.${blackoutDay?.reason} Cannot change status at this time.`
        );
        return;
      }
      if (
        employee.offerState === OfferState.WILLINGNESS_GIVEN &&
        formik.values.id
      ) {
        setStatusDialog({ open: true, employees: [employee] });
      }
    }
    if (action.name === "Reset Willingness") {
      setResetDialog({ open: true, employee });
      return;
    }
    if (action.name === "Attach Documents") {
      setAttachDocumentsDialog({
        open: true,
        employeeId: employee.employeeId,
      });
      return;
    }
    if (action.name === "Add Payment Details") {
      if (!formik.values.monetaryDetails.transactionPaymentEnabled) {
        toast.error("Payment details are not enabled for this transaction.");
        return;
      }
      setPaymentDialog({
        open: true,
        approval: false,
        employee,
      });
      return;
    }
    if (action.name === "Approve Payment Details") {
      if (!formik.values.monetaryDetails.transactionPaymentEnabled) {
        toast.error("Payment details are not enabled for this transaction.");
        return;
      }
      setPaymentDialog({
        open: true,
        approval: true,
        employee,
      });
      return;
    }
  };

  const rowData = useMemo(
    () =>
      formik.values.previewList?.filter((employee) => {
        if (
          formik.values.offerFilter.selectedEmployeeIds &&
          formik.values.offerFilter.selectedEmployeeIds.length > 0 &&
          formik.values.offerFilter.selectedEmployeeIds
            .map((e: any) => e.id || e.employeeId)
            .includes(employee.employeeId)
        ) {
          return true;
        }
        // Department filter
        if (
          formik.values.offerFilter.department &&
          formik.values.offerFilter.department.length > 0 &&
          !formik.values.offerFilter.department.includes(
            employee.employeeDepartment
          )
        ) {
          return false;
        }

        // Min vested options filter
        if (
          formik.values.offerFilter.vestedOptions &&
          formik.values.offerFilter.vestedOptions.min &&
          (employee.totalVestedOptions || 0) <
            formik.values.offerFilter.vestedOptions.min
        ) {
          return false;
        }
        // Max vested options filter
        if (
          formik.values.offerFilter.vestedOptions &&
          formik.values.offerFilter.vestedOptions.max &&
          (employee.totalVestedOptions || 0) >
            formik.values.offerFilter.vestedOptions.max
        ) {
          return false;
        }

        if (
          formik.values.offerFilter.eligibleOptions &&
          formik.values.offerFilter.eligibleOptions.min &&
          (employee.totalEligibleOptions || 0) <
            formik.values.offerFilter.eligibleOptions.min
        ) {
          return false;
        }
        // Max vested options filter
        if (
          formik.values.offerFilter.eligibleOptions &&
          formik.values.offerFilter.eligibleOptions.max &&
          (employee.totalEligibleOptions || 0) >
            formik.values.offerFilter.eligibleOptions.max
        ) {
          return false;
        }

        if (
          formik.values.offerFilter.employmentType &&
          formik.values.offerFilter.employmentType.length > 0
        ) {
          const employmentTypes = employee.employmentType || [];
          if (
            !formik.values.offerFilter.employmentType.some((type: any) =>
              employmentTypes.includes(type)
            )
          ) {
            return false;
          }
        }

        if (
          formik.values.offerFilter.tenure &&
          ((formik.values.offerFilter.tenure.min || 0) > 0 ||
            (formik.values.offerFilter.tenure.max || Infinity) < Infinity)
        ) {
          if (
            formik.values.offerFilter.tenure.min &&
            employee.tenure < formik.values.offerFilter.tenure.min
          ) {
            return false;
          }
          if (
            formik.values.offerFilter.tenure.max &&
            employee.tenure > formik.values.offerFilter.tenure.max
          ) {
            return false;
          }
        }

        if (
          formik.values.offerFilter.status &&
          formik.values.offerFilter.status.length > 0
        ) {
          const status = employee.employeeState || "";
          if (!formik.values.offerFilter.status.includes(status)) {
            return false;
          }
        }

        if (
          formik.values.offerFilter.planIds &&
          formik.values.offerFilter.planIds.length > 0
        ) {
          const filteredGrants = employee.grantRelatedDetails.filter((grant) =>
            formik.values.offerFilter.planIds
              ?.map((p) => p.id)
              .includes(grant.planId)
          );

          if (filteredGrants.length === 0) {
            return false;
          }

          employee.totalVestedOptions = filteredGrants.reduce(
            (sum, grant) => sum + (grant.vestedOptions || 0),
            0
          );
          employee.totalEligibleOptions = filteredGrants.reduce(
            (sum, grant) => sum + (grant.eligibleOptions || 0),
            0
          );

          employee.totalGrantedOptions = filteredGrants.reduce(
            (sum, grant) => sum + (grant.grantedOptions || 0),
            0
          );
          employee.totalCashExercisedOptions = filteredGrants.reduce(
            (sum, grant) => sum + (grant.cashExercisedOptions || 0),
            0
          );
          employee.totalStockExercisedOptions = filteredGrants.reduce(
            (sum, grant) => sum + (grant.stockExercisedOptions || 0),
            0
          );

          employee.grantRelatedDetails = filteredGrants;
        }

        if (
          formik.values.offerFilter.companyIds &&
          formik.values.offerFilter.companyIds.length > 0
        ) {
          const companyIds = [
            employee.companyName,
            employee.subsidiaryCompanyName,
          ];
          if (
            !formik.values.offerFilter.companyIds.some((companyId: any) =>
              companyIds.includes(companyId)
            )
          ) {
            return false;
          }
        }

        if (
          formik.values.offerFilter.excludeEmployeeIds &&
          formik.values.offerFilter.excludeEmployeeIds.length > 0
        ) {
          return !formik.values.offerFilter.excludeEmployeeIds
            .map((e) => e.id)
            .includes(employee.employeeId);
        }

        if (
          formik.values.offerFilter.participationConstraints &&
          formik.values.offerFilter.participationConstraints.length > 0
        ) {
          const constraints =
            formik.values.offerFilter.participationConstraints;
          const participatedDates = employee.participatedEventDates || [];

          if (
            !constraints ||
            constraints.length === 0 ||
            !participatedDates ||
            participatedDates.length === 0
          ) {
            return true;
          }

          for (const constraint of constraints) {
            const { interval, intervalUnit, maxFrequency } = constraint;

            const now = new Date();
            const startDate = new Date();

            switch (intervalUnit) {
              case "Days":
                startDate.setDate(now.getDate() - interval);
                break;
              case "Month":
                startDate.setMonth(now.getMonth() - interval);
                break;
              case "Quarter":
                startDate.setMonth(now.getMonth() - interval * 3);
                break;
              case "Year":
                startDate.setFullYear(now.getFullYear() - interval);
                break;
            }

            const participationCount = employee.participatedEventDates.filter(
              (date) => new Date(date) >= startDate && new Date(date) <= now
            ).length;

            if (participationCount >= maxFrequency) {
              return false;
            }
          }
          return true;
        }

        if (
          formik.values.offerFilter.nationality &&
          formik.values.offerFilter.nationality.length > 0 &&
          !formik.values.offerFilter.nationality.includes(
            employee.nationality || ""
          )
        ) {
          return false;
        }

        return true;
      }) || [],
    [formik.values.previewList, formik.values.selectedEmployeesList]
  );

  const rowData2 = useMemo(
    () =>
      formik.values.selectedEmployeesList
        ?.map((employee) => ({
          ...employee,
          actions: (
            <CTADropdown
              dropdownClassName=" xl:right-10 lg:right-8 md:right-2"
              actions={[
                {
                  name: "Delete",
                  disabled:
                    employee.offerState !== OfferState.DRAFT &&
                    employee.offerState !== OfferState.OFFERED,
                  tooltipText: "Delete Offer",
                },
                {
                  name: "Change Status",
                  disabled:
                    employee.offerState !== OfferState.WILLINGNESS_GIVEN,
                  tooltipText: "",
                },
                {
                  name: "Reset Willingness",
                  disabled: employee.offerState === OfferState.COMPLETED,
                  tooltipText:
                    employee.offerState === OfferState.COMPLETED
                      ? "Reset not available — offer is already completed"
                      : "Reset employee willingness",
                },
                {
                  name: "Attach Documents",
                  tooltipText: "",
                },
                ...(formik.values.monetaryDetails.transactionPaymentEnabled
                  ? [
                      {
                        name: "Add Payment Details",
                        tooltipText: "Add Payment Details",
                      },
                    ]
                  : []),
                ...(formik.values.monetaryDetails.transactionPaymentEnabled
                  ? [
                      {
                        name: "Approve Payment Details",
                        tooltipText: "Approve Payment Details",
                        // Disabled only when all submitted payments are already resolved
                        disabled:
                          (employee.paymentDetails?.length ?? 0) > 0 &&
                          !employee.paymentDetails?.some(
                            (p) => p.paymentStatus === "PAYMENT_PENDING"
                          ),
                      },
                    ]
                  : []),
              ]}
              onAction={(action) => handleAction(employee, action)}
            />
          ),
        }))
        .filter((employee) => employee.offerState !== OfferState.OFFERED) || [],
    [formik.values.previewList, formik.values.selectedEmployeesList]
  );

  const [selectedRows, setSelectedRows] = useState<EmployeeOfferDetails[]>([]);
  const [selectedParticipantRows, setSelectedParticipantRows] = useState<
    EmployeeOfferDetails[]
  >([]);

  const onSelectionChanged = (event: any) => {
    setSelectedRows(event.api.getSelectedRows());
  };

  const onSelectionParticipantChanged = (event: any) => {
    setSelectedParticipantRows(event.api.getSelectedRows());
  };

  const validBulkActions = [
    {
      name: "Change Status",
      disabled:
        selectedRows.length === 0 ||
        selectedRows.some((e) => e.offerState !== OfferState.WILLINGNESS_GIVEN),
    },
    {
      name: "Send Exercise Payment Reminders",
      disabled:
        selectedRows.length === 0 ||
        selectedRows.some((emp) =>
          emp.paymentDetails?.some(
            (p) =>
              p.paymentType.toLowerCase() === "exercise price" &&
              p.paymentStatus === "SUCCESS"
          )
        ),
    },
    {
      name: "Send Tax Payment Reminders",
      disabled:
        selectedRows.length === 0 ||
        selectedRows.some((emp) =>
          emp.paymentDetails?.some(
            (p) =>
              p.paymentType.toLowerCase() === "tax price" &&
              p.paymentStatus === "SUCCESS"
          )
        ),
    },
    {
      name: "Send Letter Sign Reminders",
      disabled:
        !formik.values.monetaryDetails.employeeSignatureRequired ||
        selectedRows.length === 0 ||
        selectedRows.some(
          (e) => e.offerState !== OfferState.WILLINGNESS_ACCEPTED
        ),
    },
  ];

  const validParticipationBulkActions = [
    {
      name: "Send Acceptance Reminder Emails",
      disabled:
        selectedParticipantRows.length === 0 ||
        selectedParticipantRows.every(
          (e) =>
            e.offerState !== OfferState.DRAFT &&
            e.offerState !== OfferState.OFFERED
        ),
    },
  ];

  const isValidDate = (date: string) =>
    Number.isNaN(date) &&
    new Date(date).toString() !== "Invalid Date" &&
    !Number.isNaN(Date.parse(date));

  const normaliseValue = (value: string | number) => {
    if (typeof value === "number") return value;
    if (typeof value === "string") {
      if (isValidDate(value)) return new Date(value).valueOf();
      try {
        value = value.replaceAll(",", "");
      } catch (TypeError) {
        value = value.replace(/,./g, "");
      }
      return parseFloat(value);
    }
    return value;
  };

  const sortComparator = (
    valueA: number | string,
    valueB: number | string,
    ..._: any[]
  ) => normaliseValue(valueA) - normaliseValue(valueB);

  const sideBar = useMemo<SideBarDef>(
    () => ({
      toolPanels: [
        {
          id: "columns",
          labelDefault: "Columns",
          labelKey: "columns",
          iconKey: "columns",
          toolPanel: "agColumnsToolPanel",
          minWidth: 225,
          width: 225,
          maxWidth: 225,
          toolPanelParams: {
            suppressRowGroups: true,
            suppressPivots: true,
            suppressPivotMode: true,
            suppressValues: true,
          },
        },
        {
          id: "filters",
          labelDefault: "Filters",
          labelKey: "filters",
          iconKey: "filter",
          toolPanel: "agFiltersToolPanel",
          minWidth: 180,
          maxWidth: 400,
          width: 250,
        },
      ],
    }),
    []
  );

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      wrapText: true,
      flex: 1,
      autoHeight: true,
      initialWidth: 150,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      cellClass: "multiline",
      minWidth: 150,
      columnsMenuParams: {
        suppressColumnFilter: true,
      },
      comparator: sortComparator,
      filterParams: {
        buttons: ["reset"],
        maxNumConditions: 5,
      },
      filter: true,
      resizable: true,
    }),
    []
  );

  const [open, setOpen] = useState<{ open: boolean; data?: NameAndId }>({
    open: false,
    data: undefined,
  });

  const handleDeleteClick = (employees?: NameAndId[]) => {
    if (!employees) return;
    if (formik.values.eventState !== MonetaryEventState.STARTED) {
      toast.error("You can only delete employees in STARTED state");
      return;
    }
    setDeleting(true);
    try {
      const employeeIds = _.compact(employees.map((e) => e.id));
      formik.setFieldValue(
        "previewList",
        formik.values.previewList?.filter(
          (emp) => !employeeIds.includes(emp.employeeId)
        )
      );
      const offerFilter = formik.values.offerFilter;
      if (
        formik.values.monetaryDetails.participantListMethod !== "Upload Excel"
      ) {
        const employeeIds = employees.map((e) => String(e.id || e.employeeId));
        const updatedSelected = (offerFilter.selectedEmployeeIds || []).filter(
          (e) => !employeeIds.includes(String(e.id || e.employeeId))
        );
        formik.setFieldValue(
          "offerFilter.selectedEmployeeIds",
          updatedSelected
        );
        const prevExcluded = offerFilter.excludeEmployeeIds || [];
        const newExcluded = [
          ...prevExcluded,
          ...employees.filter(
            (emp) =>
              !prevExcluded.some(
                (ex) =>
                  String(ex.id || ex.employeeId) ===
                  String(emp.id || emp.employeeId)
              )
          ),
        ];
        formik.setFieldValue("offerFilter.excludeEmployeeIds", newExcluded);
        offerFilter.excludeEmployeeIds = newExcluded;
        offerFilter.selectedEmployeeIds = updatedSelected;
      }
      deleteEmployeeFromTransaction({
        employeeIds,
        transactionId: formik.values.id || "",
        offerFilter,
      })
        .then((res) => {
          formik.setFieldValue("offerFilter", res.data.offerFilter);
          formik.setFieldValue("previewList", res.data.previewList);
          formik.setFieldValue(
            "selectedEmployeesList",
            res.data.selectedEmployeesList
          );
          setOpen({ open: false, data: undefined });
          toast.success("Employees deleted successfully!");
        })
        .catch((error) => {
          toast.error(error.response.data.errorRaw);
        });
      setOpen({ open: false, data: undefined });
    } catch (error: any) {
      toast.error(error.response?.data?.errorRaw || "Delete failed");
    } finally {
      setDeleting(false);
      setOpen({ open: false, data: undefined });
    }
  };
  const topGrid = useRef(null);
  const bottomGrid = useRef(null);

  const participantColumnDefs: ColDef[] = [
    {
      headerName: "EMPLOYEE NAME",
      width: 200,
      checkboxSelection: true, // <-- Add this
      headerCheckboxSelection: true,
      autoHeight: true,
      cellStyle: { paddingTop: "15px", lineHeight: "20px" },
      wrapText: true,
      colId: "employeeName",
      valueGetter: (params: any) => ({
        employeeName: params.data?.employeeName,
        employeeDepartment: params.data?.employeeDepartment,
        employeeDesignation: params.data?.employeeDesignation,
        hrId: params.data?.hrId,
      }),
      filter: "agMultiColumnFilter",
      menuTabs: ["filterMenuTab"],
      filterParams: {
        maxNumConditions: 5,
        buttons: ["reset"],
        filters: [
          {
            title: "Name",
            display: "subMenu",
            filter: "agSetColumnFilter",
            filterParams: {
              buttons: ["reset"],
              keyCreator: (params: any) => params.value?.employeeName,
              valueFormatter: (params: any) => params.value?.employeeName,
            },
          },
          {
            title: "Department",
            display: "subMenu",
            filter: "agSetColumnFilter",
            filterParams: {
              buttons: ["reset"],
              keyCreator: (params: any) => params.value?.employeeDepartment,
              valueFormatter: (params: any) => params.value?.employeeDepartment,
            },
          },
          {
            title: "Designation",
            display: "subMenu",
            filter: "agSetColumnFilter",
            filterParams: {
              buttons: ["reset"],
              keyCreator: (params: any) => params.value?.employeeDesignation,
              valueFormatter: (params: any) =>
                params.value?.employeeDesignation,
            },
          },
          {
            title: "Emp ID",
            display: "subMenu",
            filter: "agSetColumnFilter",
            filterParams: {
              buttons: ["reset"],
              keyCreator: (params: any) => params.value?.hrId,
              valueFormatter: (params: any) => params.value?.hrId,
            },
          },
        ],
      },
      comparator(valueA, valueB) {
        return valueA?.employeeName
          ?.toLowerCase()
          ?.localeCompare(valueB?.employeeName?.toLowerCase());
      },
      initialWidth: 200,
      minWidth: 200,
      cellRendererParams: (params: any) => ({
        employeeName: params.data.employeeName,
        employeeDesignation: params.data.employeeDesignation,
        employeeDepartment: params.data.employeeDepartment,
        employeeState: params.data.employeeState,
      }),
      cellRenderer: (params: any) =>
        params.data?.isTotalRow ? "Total" : EmployeeInfoRender(params),
      getQuickFilterText: (params: any) =>
        [
          params.data.employeeName,
          params.data.employeeDesignation,
          params.data.employeeDepartment,
          params.data.employeeState,
          params.data.hrId,
        ]
          .filter(Boolean)
          .join(" "),
    },
    {
      headerName: "VESTED OPTIONS",
      autoHeight: true,
      field: "totalVestedOptions",
      flex: 1,
      width: 80,
      filter: "agNumberColumnFilter",
      menuTabs: ["filterMenuTab"],
      valueFormatter: (params) => params.value.toLocaleString(currencyType),
    },
    {
      headerName: "ELIGIBLITY",
      autoHeight: true,
      filter: "agNumberColumnFilter",
      menuTabs: ["filterMenuTab"],
      valueGetter: (params) => {
        const percentage =
          formik.values.monetaryDetails.percentageOfNetVestedOptions || 0;
        const totalEligibleOptions = params.data.totalEligibleOptions || 0;

        return percentage > 0
          ? Math.floor(totalEligibleOptions * (percentage / 100))
          : totalEligibleOptions;
      },
      valueFormatter: (params) => params.value.toLocaleString(currencyType),
      flex: 1,
      width: 80,
    },
    {
      headerName: "STATUS",
      autoHeight: true,
      field: "offerState",
      filter: "agSetColumnFilter",
      menuTabs: ["filterMenuTab"],
      flex: 1,
      width: 80,
      cellStyle: { paddingTop: "15px", lineHeight: "20px" },
      cellRenderer: (params: any) =>
        params.data?.isTotalRow ? "" : PreviewListStatus(params.value),
    },
    {
      headerName: "NATIONALITY",
      autoHeight: true,
      field: "nationality",
      filter: "agSetColumnFilter",
      menuTabs: ["filterMenuTab"],
      flex: 1,
      width: 100,
      valueFormatter: (params: any) => params.value || "-",
    },
    {
      headerName: "",
      width: 30,
      filter: false,
      menuTabs: [],
      cellStyle: (params: any) => ({
        paddingTop: "10px",
        paddingRight: "10px",
        paddingLeft: "10px",
      }),
      cellRenderer: (params: any) =>
        params.data?.isTotalRow ? (
          ""
        ) : (
          <>
            {[OfferState.DRAFT, OfferState.OFFERED].includes(
              params.data.offerState
            ) || !params.data.offerState ? (
              <div
                onClick={() =>
                  setOpen({
                    open: true,
                    data: {
                      id: params.data.employeeId,
                      name: params.data.employeeName,
                      hrId: params.data.hrId,
                    },
                  })
                }
              >
                <Icon icon="mdi:trash" height={24} />
              </div>
            ) : (
              <></>
            )}
          </>
        ),
    },
  ];

  const exercisePricePaid: ColDef | undefined = formik.values.monetaryDetails
    .transactionPaymentEnabled
    ? {
        headerName: "EXERCISE PRICE",
        autoHeight: true,
        colId: "exercisePricePaid",
        valueGetter: (params: any) => ({
          amount:
            params.data?.transactionCompensationDetails?.totalStrikePrice || 0,
          paymentStatus:
            params.data?.paymentDetails?.find(
              (p: any) => p.paymentType.toLowerCase() === "exercise price"
            )?.paymentStatus || null,
        }),
        filter: "agMultiColumnFilter",
        menuTabs: ["filterMenuTab"],
        filterParams: {
          maxNumConditions: 5,
          buttons: ["reset"],
          filters: [
            {
              title: "Amount",
              display: "subMenu",
              filter: "agNumberColumnFilter",
              filterParams: {
                buttons: ["reset"],
                valueGetter: (params: any) =>
                  params.data?.transactionCompensationDetails
                    ?.totalStrikePrice || 0,
              },
            },
            {
              title: "Payment Status",
              display: "subMenu",
              filter: "agSetColumnFilter",
              filterParams: {
                buttons: ["reset"],
                keyCreator: (params: any) => {
                  const s = params.value?.paymentStatus;
                  if (s === "SUCCESS") return "PAID";
                  if (s === "FAILED") return "FAILED";
                  if (s === "PAYMENT_PENDING") return "PENDING APPROVAL";
                  return "NOT SUBMITTED";
                },
                valueFormatter: (params: any) => {
                  const s = params.value?.paymentStatus;
                  if (s === "SUCCESS") return "PAID";
                  if (s === "FAILED") return "FAILED";
                  if (s === "PAYMENT_PENDING") return "PENDING APPROVAL";
                  return "NOT SUBMITTED";
                },
              },
            },
          ],
        },
        flex: 1,
        width: 150,
        cellStyle: { paddingTop: "15px", lineHeight: "20px" },
        cellRenderer: (params: any) => {
          if (params.data?.isTotalRow) {
            return null;
          }
          const paymentDetails = params.data.paymentDetails || [];
          const latestExercisePrice = paymentDetails.find(
            (p: any) => p.paymentType.toLowerCase() === "exercise price"
          );
          const transactionCompensationDetails =
            params.data.transactionCompensationDetails;
          return (
            <VStack className="items-center justify-between gap-1">
              <p className="text-xs font-medium text-gray-dark">
                {formatCurrency(
                  transactionCompensationDetails?.totalStrikePrice || 0,
                  currencyType
                )}
              </p>
              <p
                className={`text-xs font-medium ${
                  latestExercisePrice?.paymentStatus === "FAILED"
                    ? "text-red-500"
                    : latestExercisePrice?.paymentStatus === "SUCCESS"
                    ? "text-[#069F10]"
                    : latestExercisePrice?.paymentStatus === "PAYMENT_PENDING"
                    ? "text-[#E29500]"
                    : params.data.offerState === OfferState.EXPIRED
                    ? ""
                    : "text-gray-400"
                }`}
              >
                {latestExercisePrice?.paymentStatus === "SUCCESS"
                  ? "PAID"
                  : latestExercisePrice?.paymentStatus === "FAILED"
                  ? "FAILED"
                  : latestExercisePrice?.paymentStatus === "PAYMENT_PENDING"
                  ? "PENDING APPROVAL"
                  : params.data.offerState === OfferState.EXPIRED
                  ? ""
                  : "NOT SUBMITTED"}
              </p>
            </VStack>
          );
        },
      }
    : undefined;

  const taxPaid: ColDef | undefined = formik.values.monetaryDetails
    .transactionPaymentEnabled
    ? {
        headerName: "TAX PRICE",
        autoHeight: true,
        colId: "taxPricePaid",
        valueGetter: (params: any) => ({
          amount:
            params.data?.transactionCompensationDetails?.totalTaxAmount || 0,
          paymentStatus:
            params.data?.paymentDetails?.find(
              (p: any) => p.paymentType.toLowerCase() === "tax price"
            )?.paymentStatus || null,
        }),
        filter: "agMultiColumnFilter",
        menuTabs: ["filterMenuTab"],
        filterParams: {
          maxNumConditions: 5,
          buttons: ["reset"],
          filters: [
            {
              title: "Amount",
              display: "subMenu",
              filter: "agNumberColumnFilter",
              filterParams: {
                buttons: ["reset"],
                valueGetter: (params: any) =>
                  params.data?.transactionCompensationDetails?.totalTaxAmount ||
                  0,
              },
            },
            {
              title: "Payment Status",
              display: "subMenu",
              filter: "agSetColumnFilter",
              filterParams: {
                buttons: ["reset"],
                keyCreator: (params: any) => {
                  const s = params.value?.paymentStatus;
                  if (s === "SUCCESS") return "PAID";
                  if (s === "FAILED") return "FAILED";
                  if (s === "PAYMENT_PENDING") return "PENDING APPROVAL";
                  return "NOT SUBMITTED";
                },
                valueFormatter: (params: any) => {
                  const s = params.value?.paymentStatus;
                  if (s === "SUCCESS") return "PAID";
                  if (s === "FAILED") return "FAILED";
                  if (s === "PAYMENT_PENDING") return "PENDING APPROVAL";
                  return "NOT SUBMITTED";
                },
              },
            },
          ],
        },
        flex: 1,
        width: 150,
        cellStyle: { paddingTop: "15px", lineHeight: "20px" },
        cellRenderer: (params: any) => {
          if (params.data?.isTotalRow) {
            return null;
          }
          const paymentDetails = params.data.paymentDetails || [];
          const latestTaxPrice = paymentDetails.find(
            (p: any) => p.paymentType.toLowerCase() === "tax price"
          );
          const transactionCompensationDetails =
            params.data.transactionCompensationDetails;
          return (
            <VStack className="items-center justify-between gap-1">
              <p className="text-xs font-medium text-gray-dark">
                {formatCurrency(
                  transactionCompensationDetails?.totalTaxAmount || 0,
                  currencyType
                )}
              </p>
              <p
                className={`text-xs font-medium ${
                  latestTaxPrice?.paymentStatus === "FAILED"
                    ? "text-red-500"
                    : latestTaxPrice?.paymentStatus === "SUCCESS"
                    ? "text-[#069F10]"
                    : latestTaxPrice?.paymentStatus === "PAYMENT_PENDING"
                    ? "text-[#E29500]"
                    : params.data.offerState === OfferState.EXPIRED
                    ? ""
                    : "text-gray-400"
                }`}
              >
                {latestTaxPrice?.paymentStatus === "SUCCESS"
                  ? "PAID"
                  : latestTaxPrice?.paymentStatus === "FAILED"
                  ? "FAILED"
                  : latestTaxPrice?.paymentStatus === "PAYMENT_PENDING"
                  ? "PENDING APPROVAL"
                  : params.data.offerState === OfferState.EXPIRED
                  ? ""
                  : "NOT SUBMITTED"}
              </p>
            </VStack>
          );
        },
      }
    : undefined;

  const columnDefs: ColDef[] = [
    {
      headerName: "EMPLOYEE NAME",
      width: 200,
      checkboxSelection: true, // <-- Add this
      headerCheckboxSelection: true,
      autoHeight: true,
      cellStyle: { paddingTop: "15px", lineHeight: "20px" },
      wrapText: true,
      colId: "employeeName",
      valueGetter: (params: any) => ({
        employeeName: params.data?.employeeName,
        employeeDepartment: params.data?.employeeDepartment,
        employeeDesignation: params.data?.employeeDesignation,
      }),
      filter: "agMultiColumnFilter",
      menuTabs: ["filterMenuTab"],
      filterParams: {
        maxNumConditions: 5,
        buttons: ["reset"],
        filters: [
          {
            title: "Name",
            display: "subMenu",
            filter: "agSetColumnFilter",
            filterParams: {
              buttons: ["reset"],
              keyCreator: (params: any) => params.value?.employeeName,
              valueFormatter: (params: any) => params.value?.employeeName,
            },
          },
          {
            title: "Department",
            display: "subMenu",
            filter: "agSetColumnFilter",
            filterParams: {
              buttons: ["reset"],
              keyCreator: (params: any) => params.value?.employeeDepartment,
              valueFormatter: (params: any) => params.value?.employeeDepartment,
            },
          },
          {
            title: "Designation",
            display: "subMenu",
            filter: "agSetColumnFilter",
            filterParams: {
              buttons: ["reset"],
              keyCreator: (params: any) => params.value?.employeeDesignation,
              valueFormatter: (params: any) =>
                params.value?.employeeDesignation,
            },
          },
        ],
      },
      comparator(valueA, valueB) {
        return valueA?.employeeName
          ?.toLowerCase()
          ?.localeCompare(valueB?.employeeName?.toLowerCase());
      },
      initialWidth: 200,
      minWidth: 200,
      cellRendererParams: (params: any) => ({
        employeeName: params.data.employeeName,
        employeeDesignation: params.data.employeeDesignation,
        employeeDepartment: params.data.employeeDepartment,
        employeeState: params.data.employeeState,
      }),
      cellRenderer: (params: any) =>
        params.data?.isTotalRow ? "Total" : EmployeeInfoRender(params),
      getQuickFilterText: (params: any) =>
        [
          params.data.employeeName,
          params.data.employeeDesignation,
          params.data.employeeDepartment,
          params.data.employeeState,
          params.data.hrId,
        ]
          .filter(Boolean)
          .join(" "),
    },
    {
      headerName: "WILLINGNESS",
      autoHeight: true,
      field: "totalWillingness",
      filter: "agNumberColumnFilter",
      menuTabs: ["filterMenuTab"],
      flex: 1,
      width: 150,
      cellStyle: { paddingTop: "15px", lineHeight: "20px" },

      cellRendererParams: (params: any) => ({
        totalWillingness: params.data.totalWillingness,
        totalEligibleOptions: (() => {
          const percentage =
            formik.values.monetaryDetails.percentageOfNetVestedOptions || 0;
          const totalEligibleOptions = params.data.totalEligibleOptions || 0;

          return percentage > 0
            ? Math.floor(totalEligibleOptions * (percentage / 100))
            : totalEligibleOptions;
        })(),
      }),
      cellRenderer: (params: any) => RequestedCellRender(params),
    },
    ...(exercisePricePaid ? [exercisePricePaid] : []),
    ...(taxPaid ? [taxPaid] : []),
    {
      headerName: "STATUS",
      autoHeight: true,
      field: "offerState",
      filter: "agSetColumnFilter",
      menuTabs: ["filterMenuTab"],
      flex: 1,
      width: 80,
      cellStyle: { paddingTop: "15px", lineHeight: "20px" },
      cellRendererParams: (params: any) => ({
        params,
      }),
      cellRenderer: (params: any) =>
        params.data?.isTotalRow ? "" : PreviewListStatus(params.value),
    },
    {
      headerName: "",
      field: "actions",
      pinned: "right",
      hide: false,
      width: 80,
      maxWidth: 80,
      filter: false,
      colId: "action-column",
      suppressNavigable: true,
      suppressColumnsToolPanel: true,
      resizable: false,
      sortable: false,
      autoHeight: true,
      menuTabs: ["columnsMenuTab"],
      cellRendererParams: ({ value }: { value: { props: any } }) =>
        value ? value.props : null,
      cellRenderer: CTADropdown,
    },
  ];

  const handleBackClick = () => {
    if (props.onBackClick) {
      props.onBackClick();
    }
  };

  useEffect(() => {
    //Need to Implement details Logic here
  }, [formik.values.previewList, formik.values.selectedEmployeesList]);

  const handleCloseTransaction = () => {
    closeTransaction(
      {
        transactionId: formik.values.id,
      },
      {
        onSuccess: (data) => {
          formik.setFieldValue("eventState", MonetaryEventState.CLOSED);
          formik.setFieldValue("previewList", data.previewList);
          formik.setFieldValue(
            "selectedEmployeesList",
            data.selectedEmployeesList
          );
        },
        onError: (error) => {},
      }
    );
  };

  const [reminderDialog, setReminderDialog] = useState<{
    open: boolean;
    type?: string;
    employees?: EmployeeOfferDetails[];
  }>({ open: false, type: undefined, employees: undefined });

  const [reminderLoading, setReminderLoading] = useState(false);

  const handleSendReminder = async (type?: string) => {
    if (!type) return;
    setReminderLoading(true);
    if (!reminderDialog.employees || reminderDialog.employees.length === 0) {
      toast.error("No employees selected for reminders.");
      setReminderLoading(false);
      return;
    }
    sendReminderEmails({
      employeeIds: reminderDialog.employees.map((emp) => emp.employeeId),
      transactionId: formik.values.id || "",
      templateType: type,
    })
      .then(() => {
        toast.success("Reminders sent!");
        setReminderDialog({
          open: false,
          type: undefined,
          employees: undefined,
        });
      })
      .catch((err) => {
        toast.error(
          err?.response?.data?.errorRaw ||
            err?.message ||
            "Failed to send reminders."
        );
      })
      .finally(() => setReminderLoading(false));
  };

  function setPinnedBottomRowData({ api }: { api: any }) {
    const data = api.rowModel.rootNode;
    const filteredData = data.childrenAfterAggFilter.map(
      (element: any) => element.data
    );

    const totalVestedOptions = filteredData?.reduce(
      (accumulator: any, data: any) => accumulator + data.totalVestedOptions,
      0
    );
    const totalEligibility = filteredData?.reduce(
      (accumulator: any, data: any) => accumulator + data.totalEligibleOptions,
      0
    );
    const totalWillingness = filteredData?.reduce(
      (accumulator: any, data: any) => accumulator + data.totalWillingness,
      0
    );

    api.setPinnedBottomRowData([
      {
        employeeName: "Total",
        totalVestedOptions,
        totalEligibleOptions: totalEligibility,
        totalWillingness,
        isTotalRow: true,
      },
    ]);
  }

  return (
    <VStack className="w-full">
      <HStack className="justify-between gap-1 pb-3 px-6 font-medium border-b-[0.5px] pt-7">
        <VStack>
          <HStack className="flex justify-between text-xl font-semibold text-black-501">
            Transaction Started
          </HStack>
          <HStack className="flex text-sm font-medium text-gray-500">
            The exercise transaction has begun. Track progress, monitor payment
            and document status, and ensure all participant actions are
            completed smoothly.
          </HStack>
        </VStack>
        <VStack className="items-end">
          <HStack className="flex justify-between text-sm font-semibold text-black-501">
            {formatDisplayDate(formik.values.endDate)}
          </HStack>
          <HStack className="flex ml-10 text-xs font-medium text-gray-500 text-start">
            Last Date to Accept
          </HStack>
        </VStack>
      </HStack>
      <StatusChangeDialog
        open={statusDialog.open}
        employees={statusDialog.employees}
        onClose={() => {
          setMetaData(undefined);
          setStatusDialog({ open: false, employees: undefined });
        }}
        onSubmit={handleStatusChange}
        loading={statusLoading}
        setMetaData={setMetaData}
        metaData={metaData}
      />
      <Dialog
        open={attachDocumentsDialog.open}
        onClose={() =>
          setAttachDocumentsDialog({ open: false, employeeId: undefined })
        }
        maxWidth="lg"
      >
        <DocumentsUploaded
          employeeId={attachDocumentsDialog.employeeId || ""}
          transactionId={formik.values.id || ""}
          onClose={() =>
            setAttachDocumentsDialog({ open: false, employeeId: undefined })
          }
        />
      </Dialog>
      <Dialog
        open={paymentDialog.open}
        onClose={() =>
          setPaymentDialog({
            open: false,
            approval: false,
            employee: undefined,
          })
        }
        maxWidth="lg"
      >
        <AddExternalPaymentDetails
          monetaryDetails={formik.values.monetaryDetails}
          employee={paymentDialog.employee}
          transactionId={formik.values.id}
          onClose={() =>
            setPaymentDialog({
              open: false,
              approval: false,
              employee: undefined,
            })
          }
          paymentDetails={paymentDialog.employee?.paymentDetails || []}
          approval={paymentDialog.approval}
        />
      </Dialog>
      <div className="w-full px-6 pb-8">
        <VStack className="px-6 py-4 ">
          <HStack className="flex gap-4 pt-6 text-base font-semibold text-center text-black-501">
            <SummaryTransactionCards
              cardTypes={
                [
                  "employees",
                  "optionsExercised",
                  "exercisePricePaid",
                ] as SummaryCardType[]
              }
              currencyType={currencyType}
            />
          </HStack>
        </VStack>
      </div>
      <div className="w-full px-6 pb-8">
        {formik.values.monetaryDetails.participantListMethod ===
          "Generate By Filters" && (
          <HStack className="w-full gap-4 px-6 py-4 border border-black rounded-md ">
            {
              <div className="flex flex-wrap gap-4">
                <FilterOptions formik={formik} disableUpdate={true} />
              </div>
            }
          </HStack>
        )}
      </div>
      <HStack className="gap-4 px-6 border-b">
        <Box
          onClick={() => {
            setShowParticipantList(!showParticipantList);
          }}
          className={`text-xs3 font-semibold cursor-pointer py-1 ${
            showParticipantList
              ? "border-b-2 border-solid border-red-500 text-red-500"
              : "text-gray-201 "
          } `}
        >
          <p>Participant List</p>
        </Box>
        <Box
          onClick={() => {
            setShowParticipantList(!showParticipantList);
          }}
          className={`text-xs3 font-semibold cursor-pointer py-1 ${
            !showParticipantList
              ? "border-b-2 border-solid border-red-500 text-red-500"
              : "text-gray-201 "
          } `}
        >
          <p>Exercise Request</p>
        </Box>
      </HStack>
      <HStack className="justify-end gap-8 px-6 pt-4">
        <div className="flex gap-2 text-xs text-center justify-evenly">
          {showParticipantList ? (
            <Center className="w-full">
              <BulkActions
                actions={validParticipationBulkActions}
                onAction={(action) => {
                  if (action.disabled) return;
                  if (action.name === "Send Acceptance Reminder Emails") {
                    setReminderDialog({
                      open: true,
                      type: "ExerciseAcceptanceReminderEmployee",
                      employees: selectedParticipantRows,
                    });
                  }
                }}
              />
            </Center>
          ) : (
            <Center className="w-full">
              <BulkActions
                actions={validBulkActions}
                onAction={(action) => {
                  if (action.disabled) return;
                  if (action.name === "Change Status") {
                    setStatusDialog({
                      open: true,
                      employees: selectedRows,
                    });
                  } else if (
                    action.name === "Send Exercise Payment Reminders"
                  ) {
                    setReminderDialog({
                      open: true,
                      type: "ExerciseStrikePriceReminderEmployee",
                      employees: selectedRows,
                    });
                  } else if (action.name === "Send Tax Payment Reminders") {
                    setReminderDialog({
                      open: true,
                      type: "ExerciseTaxPriceReminderEmployee",
                      employees: selectedRows,
                    });
                  } else if (action.name === "Send Letter Sign Reminders") {
                    setReminderDialog({
                      open: true,
                      type: "ExerciseLetterSignReminderEmployee",
                      employees: selectedRows,
                    });
                  }
                }}
              />
            </Center>
          )}
          <SearchInput
            className=""
            placeholder={`Search`}
            onChange={(e: any) => {
              gridApi.current.api.setQuickFilter(e.target.value);
            }}
          />
          <ButtonPrimary
            onClick={() => {
              if (formik.values.eventState !== MonetaryEventState.STARTED) {
                toast.error("You can only Add employees in STARTED state");
                return;
              }
              setDialog({ open: true, data: undefined });
            }}
          >
            Add Participant
          </ButtonPrimary>
        </div>
      </HStack>
      <Dialog
        open={dialog.open}
        onClose={() => setDialog({ open: false, data: undefined })}
        maxWidth="lg"
      >
        <AddParticipantPopUp
          onClose={() => setDialog({ open: false, data: undefined })}
        />
      </Dialog>
      <Dialog
        open={reminderDialog.open}
        onClose={() =>
          setReminderDialog({
            open: false,
            type: undefined,
            employees: undefined,
          })
        }
        maxWidth="lg"
      >
        <ReminderEmailDialog
          open={reminderDialog.open}
          employees={reminderDialog.employees}
          onClose={() =>
            setReminderDialog({
              open: false,
              type: undefined,
              employees: undefined,
            })
          }
          onSubmit={() => handleSendReminder(reminderDialog.type)}
          loading={reminderLoading}
          notesElement={getReminderEmailNotes(emailSettings)}
        />
      </Dialog>
      <AlertDialog
        open={open.open}
        message="Are you sure want to delete the employee"
        primaryActionText="Delete"
        onPrimaryAction={() =>
          handleDeleteClick([open.data || { id: "", name: "" }])
        }
        onSecondaryAction={() => setOpen({ open: false, data: undefined })}
      ></AlertDialog>
      <ResetWillingnessDialog
        open={resetDialog.open}
        employeeName={resetDialog.employee?.employeeName}
        onClose={() => setResetDialog({ open: false, employee: undefined })}
        loading={resetLoading}
        onSubmit={async (reason) => {
          if (!resetDialog.employee) return;
          setResetLoading(true);
          try {
            const res = await resetEmployeeWillingness({
              transactionId: formik.values.id || "",
              employeeIds: [resetDialog.employee.employeeId],
              reason,
            });
            formik.setFieldValue("previewList", res.data.previewList);
            formik.setFieldValue(
              "selectedEmployeesList",
              res.data.selectedEmployeesList
            );
            toast.success(
              "Willingness reset successfully. The employee can now re-submit their exercise request."
            );
            setResetDialog({ open: false, employee: undefined });
          } catch (err: any) {
            const msg =
              err?.response?.data?.errorRaw ||
              err?.response?.data?.message ||
              err?.message ||
              "Failed to reset willingness";
            toast.error(msg);
          } finally {
            setResetLoading(false);
          }
        }}
      />
      {showParticipantList ? (
        <HStack className="justify-between w-full pt-6 pb-6">
          <Box
            style={{
              height: `${
                rowData.length <= 3
                  ? "600"
                  : rowData.length >= 10
                  ? "600"
                  : rowData.length * 100
              }px`,
              width: "100%",
              minWidth: 0,
            }}
            className="w-full h-full max-h-full overflow-x-auto bg-black ag-theme-material"
          >
            <AgGridReact
              rowClass={
                "border-t border-dashed cursor-pointer hover:bg-slate-50 "
              }
              getRowStyle={(params) => {
                if (params.data?.isTotalRow) {
                  return {
                    fontWeight: 600,
                  };
                }
                return undefined;
              }}
              onRowDataUpdated={setPinnedBottomRowData}
              alwaysMultiSort
              onGridReady={onGridReady}
              animateRows={true}
              ref={topGrid}
              sideBar={sideBar}
              defaultColDef={defaultColDef}
              rowData={rowData}
              onSelectionChanged={onSelectionParticipantChanged}
              suppressCopyRowsToClipboard={true}
              suppressCopySingleCellRanges={true}
              suppressCellFocus={true}
              alignedGrids={
                bottomGrid.current ? [bottomGrid.current] : undefined
              }
              columnDefs={participantColumnDefs}
              suppressRowTransform={true}
              rowMultiSelectWithClick={true}
              rowSelection="multiple"
              suppressRowClickSelection={true}
              overlayNoRowsTemplate={
                '<span style="padding: 10px; border: 2px solid #444; background: lightgoldenrodyellow;   margin-top: 50px;">No Rows To Show</span>'
              }
            ></AgGridReact>
          </Box>
        </HStack>
      ) : (
        <HStack className="justify-between w-full pt-6 pb-6">
          <Box
            style={{
              height: `${
                rowData.length <= 3
                  ? "300"
                  : rowData.length >= 10
                  ? "600"
                  : rowData.length * 100
              }px`,
              width: "100%",
              minWidth: 0,
            }}
            className="w-full h-full max-h-full overflow-x-auto bg-black ag-theme-material"
          >
            <AgGridReact
              getRowStyle={(params) => {
                if (params.data?.isTotalRow) {
                  return {
                    fontWeight: 600,
                  };
                }
                return undefined;
              }}
              onRowDataUpdated={setPinnedBottomRowData}
              rowClass={
                "border-t border-dashed cursor-pointer hover:bg-slate-50 "
              }
              alwaysMultiSort
              onGridReady={onGridReady}
              animateRows={true}
              sideBar={sideBar}
              defaultColDef={defaultColDef}
              rowData={rowData2}
              rowSelection="multiple"
              onSelectionChanged={onSelectionChanged}
              suppressCopyRowsToClipboard={true}
              suppressCopySingleCellRanges={true}
              suppressCellFocus={true}
              suppressMenuHide={true}
              alignedGrids={
                bottomGrid.current ? [bottomGrid.current] : undefined
              }
              columnDefs={columnDefs}
              ref={topGrid}
              suppressRowTransform={true}
              rowMultiSelectWithClick={true}
              suppressRowClickSelection={true}
              overlayNoRowsTemplate={
                '<span style="padding: 10px; border: 2px solid #444; background: lightgoldenrodyellow;   margin-top: 50px;">No Rows To Show</span>'
              }
            ></AgGridReact>
          </Box>
        </HStack>
      )}
      <HStack className="justify-between w-full p-6">
        <ButtonPrimary1 onClick={handleBackClick}>Back</ButtonPrimary1>
        {formik.values.eventState === MonetaryEventState.STARTED ? (
          <HStack className="gap-4">
            <ButtonPrimary
              onClick={handleCloseTransaction}
              loading={isClosing}
              disabled={adding || deleting || isClosing}
            >
              Close Window
            </ButtonPrimary>
          </HStack>
        ) : (
          <HStack className="gap-4">
            <ButtonPrimary
              onClick={() => {
                props.onStepChange();
              }}
            >
              Next
            </ButtonPrimary>
          </HStack>
        )}
      </HStack>
    </VStack>
  );
};

export default TransactionStartedDetails;
